/**
 * `numbers` — `NumbersValue` (`specs/M2-tier1-column-types.md` §5.5, `[09§A.7 numbers]`).
 *
 * Verbatim from the notes: *"The value is a JSON string — not a nested object and not a JSON
 * number — in every doc example"*: `{"numbers": "42.5"}`. All 535 populated cells sampled decode
 * to a **string** `[cvs§2 numbers]`, and monday's `text` is that string verbatim — the recorded
 * corpus has `"3.00"` -> `"3.00"` and `"10.50"` -> `"10.50"` even though the column's
 * `unit.precision` is 2. So:
 *
 * - `parseValue` (user / API input) canonicalises via `String(Number(x))`;
 * - **`fromMondayValue` (import) preserves the source string byte-for-byte**, so an imported
 *   `"1.50"` re-exports as `"1.50"`;
 * - `toText` never applies unit, separator or precision — `formatNumbers()` does that, for the
 *   cell and the footer only.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
import { numbersFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/**
 * monday ships a leading-dot decimal for a cell typed as `.15`: 9 real cells account-wide hold
 * `".15"` / `".5"` (T-X2 hook 7, `specs/M12-X2-importer.md` X2-10). `Number(".15")` is `0.15`,
 * so the value was always representable — only this regex refused it.
 */
const DECIMAL = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;
export const numbersValueSchema = z
    .string()
    .refine((value) => DECIMAL.test(value) && Number.isFinite(Number(value)), {
    message: 'expected a decimal number as a string',
});
/** `unit.precision`: -1 means "automatic", otherwise 0-5 `[09§A.7]`. */
export const numbersUnitSchema = z.looseObject({
    symbol: z.string().nullable().optional(),
    custom_unit: z.string().nullable().optional(),
    direction: z.enum(['left', 'right']).optional(),
    precision: z.number().int().min(-1).max(5).optional(),
});
export const numbersSettingsSchema = z.looseObject({
    unit: numbersUnitSchema.optional(),
    hide_footer: z.boolean().optional(),
});
function readDecimal(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'number') {
        if (!Number.isFinite(raw))
            throw new ColumnValueError('numbers', 'value is not finite');
        return String(raw);
    }
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed === '')
            return null;
        if (!DECIMAL.test(trimmed) || !Number.isFinite(Number(trimmed))) {
            throw new ColumnValueError('numbers', `"${raw}" is not a number`);
        }
        return trimmed;
    }
    if (typeof raw === 'object') {
        const record = raw;
        if ('value' in record)
            return readDecimal(record.value);
    }
    throw new ColumnValueError('numbers', `expected a number or numeric string, received ${typeof raw}`);
}
export const numbersColumn = defineColumnType({
    type: 'numbers',
    title: 'Numbers',
    description: 'A number with an optional unit, precision and group summary.',
    apiFragmentName: 'NumbersValue',
    legacyTypeName: 'numeric',
    idPrefix: 'numbers',
    valueSchema: numbersValueSchema,
    settingsSchema: numbersSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: numbersSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: numbersFilterConditions,
    filterOperators: operatorsOf(numbersFilterConditions),
    aggregations: ['sum', 'avg', 'median', 'min', 'max', 'count', 'count_empty', 'none'],
    emptyText: '',
    parseValue(raw) {
        const decimal = readDecimal(raw);
        // Canonicalise "007.50" -> "7.5" so equal numbers compare equal as strings too.
        return decimal === null ? null : String(Number(decimal));
    },
    parseSimpleValue(raw) {
        const decimal = readDecimal(raw);
        return decimal === null ? null : String(Number(decimal));
    },
    toText(value) {
        // monday's `text` is the plain number: no unit, no thousands separator, no forced precision.
        // The canonical value is a **string** (`"1.50"` stays `"1.50"`), but a legacy or
        // hand-inserted blob can hold a JSON number, and `toText` is contractually `string | null`.
        if (value === null || value === undefined)
            return '';
        return typeof value === 'string' ? value : String(value);
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        return { text: textShadow(value), number: Number(value), date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        // "7.50" equals "7.5".
        return Number(a) === Number(b);
    },
    toActivityValue(value, settings) {
        // `[ba§2]`: `{"unit": null, "value": 70}`.
        if (value === null)
            return null;
        return { unit: settings.unit?.symbol ?? null, value: Number(value) };
    },
    fromMondayValue(raw) {
        // Import preserves the source string byte-for-byte: "1.50" stays "1.50".
        return readDecimal(raw);
    },
    toMondayValue(value) {
        return value;
    },
    toSimpleValue(value) {
        return value ?? '';
    },
    fromMondaySettings(raw) {
        return numbersSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value, settings) {
        // `symbol` / `direction` come from `settings.unit` and are null when unset `[cvs§2 numbers]`.
        return {
            number: value === null ? null : Number(value),
            symbol: settings.unit?.symbol ?? null,
            direction: settings.unit?.direction ?? null,
        };
    },
    /**
     * Recorded: `{"value": 99.95, "unit": {"symbol": "$", "direction": "left", "precision": 2}}`
     * `[whk§7]`. The number is a JSON **number** here while `ColumnValue.value` for the same cell
     * is the string `"99.95"` `[cvs§2 numbers]`, and the column's unit settings ride along
     * (omitted entirely when the column has none — the recorded probe column had one).
     */
    toWebhookValue(value, settings) {
        if (value === null)
            return null;
        const numeric = Number(value);
        return {
            value: Number.isFinite(numeric) ? numeric : null,
            ...(settings.unit === undefined ? {} : { unit: settings.unit }),
        };
    },
    bucketsOf(value, settings) {
        if (value === null || value === undefined)
            return [];
        return [{ key: Number(value), label: this.toText(value, settings) ?? String(value) }];
    },
    filterSql(ctx) {
        return defaultFilterSql('number', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
/** UI helper: render `value` with the column's unit settings (cell + footer, never `text`). */
export function formatNumbers(value, settings) {
    if (value === null)
        return '';
    const unit = settings.unit;
    const precision = unit?.precision;
    const numeric = Number(value);
    // precision -1 is "automatic": render the stored digits, do not pad `[09§A.7]`.
    const body = precision === undefined || precision < 0 ? String(numeric) : numeric.toFixed(precision);
    const symbol = unit?.symbol === 'custom' ? (unit.custom_unit ?? '') : (unit?.symbol ?? '');
    if (symbol === null || symbol === '')
        return body;
    return unit?.direction === 'left' ? `${symbol}${body}` : `${body} ${symbol}`;
}
//# sourceMappingURL=index.js.map